import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Alert as RNAlert, Platform } from 'react-native';
import type { AlertButton as RNAlertButton } from 'react-native';

import { Portal, Dialog, Button, Text, useTheme } from '@/components/ui';
import { alertStyles } from '@/styles/alert';
import { setCustomAlertImplementation } from '@/utils/alert';

// Types for our alert API
type AlertButton = RNAlertButton;

type AlertOptions = {
	cancelable?: boolean;
	onDismiss?: () => void;
};

// Define the context shape
type AlertContextType = {
	alert: (title: string, message?: string, buttons?: AlertButton[], options?: AlertOptions) => void;
};

// Create the context
const AlertContext = createContext<AlertContextType | null>(null);

// Custom hook to use our alert
export const useAlert = (): AlertContextType => {
	const context = useContext(AlertContext);
	if (!context) {
		throw new Error('useAlert must be used within an AlertProvider');
	}
	return context;
};

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const theme = useTheme();
	const [visible, setVisible] = useState(false);
	const [title, setTitle] = useState('');
	const [message, setMessage] = useState('');
	const [dialogButtons, setDialogButtons] = useState<AlertButton[]>([]);
	const [options, setOptions] = useState<AlertOptions>({});

	const hideDialog = useCallback(() => {
		setVisible(false);
		if (options.onDismiss) {
			options.onDismiss();
		}
	}, [options]);

	const alert = useCallback((
		title: string,
		message?: string,
		buttons?: AlertButton[],
		options?: AlertOptions,
	) => {
		// On native platforms, use the built-in Alert
		if (Platform.OS !== 'web') {
			RNAlert.alert(title, message, buttons, options);
			return;
		}

		// On web, show our custom dialog
		setTitle(title);
		setMessage(message || '');
		setDialogButtons(buttons || [{ text: 'OK' }]);
		setOptions(options || {});
		setVisible(true);
	}, []);

	// Register our custom alert implementation
	useEffect(() => {
		setCustomAlertImplementation(alert);
	}, [alert]);

	return (
		<AlertContext.Provider value={{ alert }}>
			{children}

			{/* Only render the Dialog on web platform */}
			{Platform.OS === 'web' && (
				<Portal>
					<Dialog
						visible={visible}
						onDismiss={options.cancelable ? hideDialog : undefined}
						style={[
							alertStyles.desktopDialog,
						]}
					>
						<Dialog.Title>{title}</Dialog.Title>

						{message ? <Dialog.Content>
							<Text variant="bodyMedium">{message}</Text>
						</Dialog.Content> : null}

						<Dialog.Actions>
							{dialogButtons.map((button, index) => (
								<Button
									style={alertStyles.button}
									key={index}
									mode={button.style === 'destructive' ? 'contained' : 'text'}
									buttonColor={button.style === 'destructive' ? theme.colors.error : undefined}
									textColor={button.style === 'destructive' ? theme.colors.onError : undefined}
									onPress={() => {
										hideDialog();
										if (button.onPress) {
											button.onPress();
										}
									}}
								>
									{button.text || 'OK'}
								</Button>
							))}
						</Dialog.Actions>
					</Dialog>
				</Portal>
			)}
		</AlertContext.Provider>
	);
};
